import { store, StoreState } from '../../Store';
import { includes } from 'lodash-es';
import {
  FieldConstants,
  GPSFieldConstant,
  CorpGCSSARoles,
} from '../../Common/Models/FieldConstants';
import { RoleScope, TenantAuthorization, UserContext } from 'src/Models/UserProfile';
import { Flights, UATConstants } from './UATConstants';
import { LocalTenantConfig } from 'src/tenantConfig';
import { ITenant, TenantProvider,SGSRegionsTransformResult } from 'src/tenantConfig/interface';
import { createSelector } from '@reduxjs/toolkit';
import { TenantAccessLevel, updateTenantFeatures } from 'src/utils/RBACTenantCheckerUtils';
import { BusinessProcessConstants } from 'src/manageAction/constants/GPS';

const selectVacTenant = (state: StoreState) => state.vacTenant;
const selectUserProfile = (state: StoreState) => state.userProfile;

export const CanAddSharedView = () => {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;
  return includes(userProfile.flights, FieldConstants.createSharedViewFlightName);
};

export const userCanAddSharedView: boolean = CanAddSharedView();

export const CanAddSystemView = () => {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;
  return includes(userProfile.flights, FieldConstants.createSystemViewFlightName);
};
export const userCanAddSystemView: boolean = CanAddSystemView();

export const userCanOnlyEditAndCopySystemView: boolean = !CanAddSystemView();

export const CanViewOCPFields = () => {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;
  return includes(userProfile.flights, FieldConstants.OCPFlight);
};
export const userCanViewOCPFields: boolean = CanViewOCPFields();

export const DoesUserHasRaidAccess = () => {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;
  return includes(userProfile.flights, FieldConstants.raidAccess);
};

export const doesUserHasRaidAccess: boolean = DoesUserHasRaidAccess();

export const canGPSUserHasInvestmentsOwner = () => {
  return userGpsHasRoleScope('GPS Investments', GPSFieldConstant.GPSInvestmentsOwner);
};

// export const canGPSUserHasInvestmentsOwner: boolean = CanGPSUserHasInvestmentsOwner();

export const canGPSUserHasInvestmentsAdmin = () => {
  return userGpsHasRoleScope('GPS Investments', GPSFieldConstant.GPSInvestmentsAdmin);
};
// export const canGPSUserHasInvestmentsAdmin: boolean = CanGPSUserHasInvestmentsAdmin();

export const canGPSUserHasInvestmentsEdit = () => {
  return userGpsHasRoleScope('GPS Investments', GPSFieldConstant.GPSInvestmentsEdit);
};

export const canGPSUserHasInvestmentsBPO = () => {
  return userGpsHasRoleScope('GPS Investments', GPSFieldConstant.GPSInvestmentsBPO);
};

//export const canGPSUserHasInvestmentsEdit: boolean = CanGPSUserHasInvestmentsEdit();

export const isRoBAuthorosedUser = (scope: string) => {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;

  const tenantAuthorization = userProfile.authorization?.TenantAuthorizations.find(
    (authorization) => authorization.Tenant === UATConstants.PlaceholdersAndTitles.MCAPSLeaderRoB
  );
  if (tenantAuthorization) {
    const roleScopes = tenantAuthorization?.RoleScopeMap?.reduce<string[]>(
      (scopes, role) => scopes.concat(role.Scopes),
      []
    );
    return roleScopes.includes(scope);
  }
};

export function userRoBHasRoleScope(): string[] {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;
  const tenantAuthorization = userProfile.authorization?.TenantAuthorizations.find(
    (authorization) => authorization.Tenant === UATConstants.PlaceholdersAndTitles.MCAPSLeaderRoB
  );

  if (tenantAuthorization) {
    const roleScopes = tenantAuthorization?.RoleScopeMap?.reduce<string[]>(
      (scopes, role) => scopes.concat(role.Scopes),
      []
    );
    return roleScopes;
  }
  return [];
}

export function RoBTenantRolesScopes(): Array<RoleScope> {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;

  const tenantAuthorization = userProfile.authorization?.TenantAuthorizations.find(
    (authorization) => authorization.Tenant === UATConstants.PlaceholdersAndTitles.MCAPSLeaderRoB
  );

  if (tenantAuthorization) {
    return tenantAuthorization.RoleScopeMap;
  }
  return [];
}

export function getRoBTenantId(): TenantAuthorization | undefined {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;

  const tenantAuthorization = userProfile.authorization?.TenantAuthorizations.find(
    (authorization) => authorization.Tenant === UATConstants.PlaceholdersAndTitles.MCAPSLeaderRoB
  );

  return tenantAuthorization;
}

export function getTenantAuthorization(): TenantAuthorization | null {
  try {
    const storeState = store.getState() as StoreState;
    if (!storeState || !storeState.userProfile) {
      return null;
    }

    const userProfile = storeState.userProfile;
    const tenantId = getTenantId();

    if (!tenantId || !userProfile.authorization?.TenantAuthorizations) {
      return null;
    }

    return (
      userProfile.authorization.TenantAuthorizations.find(
        (authorization) => authorization.TenantID === tenantId
      ) || null
    );
  } catch (error) {
    return null;
  }
}

export function hasCorpGCSSAUserRole(): boolean {
  try {
    const storeState = store.getState() as StoreState;
    if (!storeState?.userProfile?.authorization?.TenantAuthorizations) {
      return false;
    }

    const corpGCSSAAuth = storeState.userProfile.authorization.TenantAuthorizations.find(
      (authorization) => authorization.Tenant === 'Corp GCS SA'
    );

    return (
      corpGCSSAAuth?.RoleScopeMap?.some(
        (role: RoleScope) => role.Role === CorpGCSSARoles.CorpGCSSAUser
      ) ?? false
    );
  } catch {
    return false;
  }
}

export function getRoBRole(role: string): boolean {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;

  const tenantAuthorization = userProfile.authorization?.TenantAuthorizations.find(
    (authorization) => authorization.Tenant === UATConstants.PlaceholdersAndTitles.MCAPSLeaderRoB
  );
  if (tenantAuthorization) {
    const roleScopes: string[] = tenantAuthorization?.RoleScopeMap?.reduce<string[]>(
      (scopes, role) => scopes.concat(role.Role),
      []
    );
    return roleScopes.includes(role);
  }
  return false;
}

export function getAzureIsvRole(role: string): boolean {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;
  const tenantId = getTenantId();
  const tenantAuthorization = userProfile.authorization?.TenantAuthorizations.find(
    (authorization) => authorization.TenantID === tenantId
  );
  if (tenantAuthorization) {
    const roleScopes: string[] = tenantAuthorization?.RoleScopeMap?.reduce<string[]>(
      (scopes, role) => scopes.concat(role.Role),
      []
    );
    return roleScopes.includes(role);
  }
  return false;
}

function userGpsHasRoleScope(tenantName: string, scope: string): boolean {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;

  const tenantAuthorization = userProfile.authorization?.TenantAuthorizations.find(
    (authorization) => authorization.Tenant === tenantName
  );

  if (tenantAuthorization) {
    const roleScopes = tenantAuthorization.RoleScopeMap?.reduce<string[]>(
      (scopes, role) => scopes.concat(role.Scopes),
      []
    );
    return roleScopes.includes(scope);
  }

  return false;
}

export function getTenantDetail(tenantId: string) {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;
  const tenantDetails = userProfile.authorization?.TenantAuthorizations.find(
    (tenantDetails) => tenantDetails.TenantID === tenantId
  );
  if (tenantDetails) {
    return tenantDetails.TenantID;
  } else {
    return null;
  }
}

export function getTenantList(): any {
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile; // Ensure vacTenant is always an array
  const vacTenant: ITenant[] = Array.isArray(storeState.vacTenant) ? storeState.vacTenant : []; // Filter and map only if vacTenant is an array

  const getRequiredTenant = vacTenant
    .filter((item) => item?.Default === true && item?.Enabled === true)
    .map((item) => item);
  //Extract only Default True and Enable True records from tenat API response
  const tenantList =
    userProfile.authorization?.TenantAuthorizations.map((item) => {
      return { ...item, ...vacTenant.find((t) => t.TenantID === item.TenantID) };
    }) || [];
  //Extract tenantAuthrizations records from UserProfie API response
  const combinedArray = [...getRequiredTenant, ...tenantList];

  // Filter out tenants with RequiresAuth enabled that are not in TenantAuthorizations
  // If a tenant is flighted, check the appropriate flight for visibility.
  const filteredArray = combinedArray.filter((tenant) => {
    if (tenant.TenantID === UATConstants.TenantIds.MEA) {
      return userProfile?.flights?.includes(Flights.MEABedrockAccess);
    }
    const isTenantFlighted: boolean = UATConstants.FlightedItems?.includes(tenant.TenantID);
    if (isTenantFlighted) {
      return userProfile?.flights?.includes(Flights.ArtemisGA);
    }

    if (!tenant.Default) {
      return userProfile.authorization?.TenantAuthorizations.some(
        (authTenant) => authTenant.TenantID === tenant.TenantID
      );
    }
    return true;
  });

  // Merge both arrays response and eliminate duplicate records, ensuring only unique TenantID values are retained, as tenant names may sometimes overlap in the response.
  const uniqueTenants = Array.from(
    new Map(filteredArray.map((item) => [item.TenantID, item])).values()
  );

  return uniqueTenants;
}

export type VisibleTenant = ITenant | (TenantAuthorization & Partial<ITenant>);

/**
 * Combines `vacTenant` and `TenantAuthorizations` from `userProfile` to produce a unique list of tenants.
 * @param vacTenant - The `vacTenant` array from the Redux store.
 * @param userProfile - The `userProfile` object from the Redux store.
 * @returns A unique list of tenants.
 */
export const getUniqueTenants = (vacTenant: ITenant[] | null | undefined, userProfile: UserContext): VisibleTenant[] => {
  // Ensure `vacTenant` is always an array
  const vacTenantArray: ITenant[] = Array.isArray(vacTenant) ? vacTenant : [];

  // Extract tenants with `Default === true` and `Enabled === true`
  const requiredTenants: ITenant[] = vacTenantArray.filter(
    (item) => item?.Default === true && item?.Enabled === true
  );

  // Combine `vacTenant` with `TenantAuthorizations` from `userProfile`
  const tenantAuthorizations: VisibleTenant[] =
    userProfile?.authorization?.TenantAuthorizations.map((item) => {
      return { ...item, ...vacTenantArray.find((t) => t.TenantID === item.TenantID) };
    }) || [];

  // Merge both arrays and eliminate duplicates by `TenantID`
  const combinedArray: VisibleTenant[] = [...requiredTenants, ...tenantAuthorizations];

  // Filter out tenants with RequiresAuth enabled that are not in TenantAuthorizations
  // If a tenant is flighted, check the appropriate flight for visibility.
  const filteredArray = combinedArray.filter((tenant) => {
    if (tenant.TenantID === UATConstants.TenantIds.MEA) {
      return userProfile?.flights?.includes(Flights.MEABedrockAccess);
    }
    const isTenantFlighted: boolean = UATConstants.FlightedItems?.includes(tenant.TenantID);
    if (isTenantFlighted) {
      return userProfile?.flights?.includes(Flights.ArtemisGA);
    }

    if (!tenant.Default) {
      return userProfile.authorization?.TenantAuthorizations.some(
        (authTenant) => authTenant.TenantID === tenant.TenantID
      );
    }
    return true;
  });

  const uniqueTenants: VisibleTenant[] = Array.from(
    new Map(filteredArray.map((item) => [item.TenantID, item])).values()
  );

  return uniqueTenants;
};

// Selector to get the combined tenant list
export const selectTenants = createSelector(
  [selectVacTenant, selectUserProfile],
  getUniqueTenants
);

export function userHasRoleScope(): boolean {
  const TenantAuthorizations = getTenantList();
  return Array.isArray(TenantAuthorizations) && TenantAuthorizations.length > 0;
}

export function getTenantId(): any {
  const urlParams = new URLSearchParams(new URL(window.location.href).search);
  const tenantId = urlParams.get('tenantId');
  const storeState = store.getState() as StoreState;
  const vacTenantData = storeState.vacTenant;
  const tenant = Object.values(vacTenantData)?.find((tenant) => tenant?.TenantID === tenantId);
  return tenant ? tenant.TenantID : null;
}

export function getHubTitle(tenantConfig: any) {
  const tenantId = getTenantId();
  return tenantConfig?.[tenantId] ? tenantConfig?.[tenantId]?.FeatureConfig?.Hub?.Title : null; // Returns the TITLE if found, otherwise null
}

export function getTenantShortName() {
  const tenantData = getTenantList();
  // Returns the ShortName if found, otherwise empty string
  return tenantData ? tenantData.ShortName : '';
}

export function getCreateActionMenuTitle(tenantConfig: any) {
  let getCreateMenuTitle = [];
  const tenantId = getTenantId();
  const getFutureConfigData = tenantConfig?.[tenantId]?.FeatureConfig;
  // Extract title
  getCreateMenuTitle = getFutureConfigData?.Hub?.Create?.map((item) => item.Text) || [];
  return getCreateMenuTitle;
}

export function getUpdatedFeatures() {
  const tenantId = getTenantId();
  const storeState = store.getState() as StoreState;
  const userProfile = storeState.userProfile;

  // Find the tenant authorization for the current tenant
  const tenantAuthorization = userProfile?.authorization?.TenantAuthorizations?.find(
    (authorization) => authorization.TenantID === tenantId
  );

  let features: TenantAccessLevel = {
    editAccess: true,
    accessLevel: [],
  };

  if (tenantAuthorization) {
    const allScopes =
      tenantAuthorization.RoleScopeMap?.flatMap((roleScope) => roleScope.Scopes) || [];

    return (features =
      tenantAuthorization.Tenant === 'GPS Investments'
        ? updateTenantFeatures(tenantAuthorization.Tenant, allScopes)
        : features);
  }
  return features;
}

export function getCreateActionMenu(tenantConfig: any) {
  let getCreateMenuKeys = [];
  const tenantId = getTenantId();
  let features: TenantAccessLevel = {
    editAccess: true,
    accessLevel: [],
  };
  const getFutureConfigData = tenantConfig?.[tenantId]?.FeatureConfig;
  // Extract initial keys
  getCreateMenuKeys = getFutureConfigData?.Hub?.Create?.map((item) => item.Key) || [];

  features = getUpdatedFeatures();

  //Adding Bulk Upload and Download Template keys to the Create Menu, if they are enabled in the tenant config
  getCreateMenuKeys =
    getFutureConfigData?.Hub?.BulkUpload && features.editAccess
      ? [...getCreateMenuKeys, UATConstants.CreateActionMenuKey.BulkImportActions]
      : getCreateMenuKeys;

  getCreateMenuKeys =
    getFutureConfigData?.Hub?.DownloadTemplate && features.editAccess
      ? [...getCreateMenuKeys, UATConstants.CreateActionMenuKey.DownloadTemplate]
      : getCreateMenuKeys;
  return getCreateMenuKeys;
}

export function getArtemisTenantAreaPaths(): string[] {
  const storeState = store.getState() as StoreState;
  const vacTenant = Array.isArray(storeState.vacTenant) ? storeState.vacTenant : [];
  const filteredTenants = vacTenant.filter((tenant) => tenant.ArtemisCall === true);
  const areaPathList = filteredTenants.reduce<Record<string, string[]>>((acc, tenant) => {
    const areaPath = tenant.AreaPathMap;
    if (areaPath) {
      Object.entries(areaPath).forEach(([key, value]) => {
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(...value);
      });
    }
    return acc;
  }, {});
  return areaPathList.Paths || [];
}

export function getCurrentTenantAreaPathsToInclude(includeTenantId: any): string[] {
  const storeState = store.getState() as StoreState;
  const vacTenant = Array.isArray(storeState.vacTenant) ? storeState.vacTenant : [];
  const currentTenant = vacTenant.find((tenant) => tenant.TenantID === includeTenantId);
  return currentTenant?.AreaPathMap?.Paths || [];
}

//IMPORTANT: Use only when the component belongs to both the old and new store.
export const getTenantConfigFromVacTenant = (tenantId: string) => {
  const localTenantConfig = LocalTenantConfig?.[tenantId];

  const storeState = store.getState() as StoreState;
  const vacTenantData = storeState.vacTenant;
  const tenant = Object.values(vacTenantData)?.find((tenant) => tenant?.TenantID === tenantId);

  if (!tenant) {
    return {};
  }

  const tenantConfig = {
    ...localTenantConfig,
    provider: tenant?.ArtemisCall ? TenantProvider.ARTEMIS : TenantProvider.ADO,
  };

  return tenantConfig;
};

// Exportable function for GPS BPO disable logic
// Locks editing for ALL users when stage is Completed, except GPS Admins who retain full access
export function isGPSBPODisable(
  gps_business: string | undefined,
  gps_stage: string | undefined
): boolean {
  const isCommercial = gps_business === UATConstants.GPSInvestmentTitles.Commercial;
  const isCompleted = gps_stage === BusinessProcessConstants.Completed;
  const isAdmin = canGPSUserHasInvestmentsAdmin();
  return isCommercial && isCompleted && !isAdmin;
}

export function isGPSCompletedDateReadOnly(
  gpsStage: string | undefined,
  state: string | undefined,
  savedStage?: string
): boolean {
  const isCompleted = [gpsStage, state, savedStage].some(
    (value) => value?.trim().toLowerCase() === BusinessProcessConstants.Completed.toLowerCase()
  );
  return isCompleted && !canGPSUserHasInvestmentsAdmin();
}

// Disables editing for Editor role users when stage is Implementation and PIR decision is Approved to Offer (Launch)
// BPO and Admin users retain full access
export function isGPSEditorDisableForLaunch(
  gps_stage: string | undefined,
  gps_pirDecision: string | undefined
): boolean {
  const isImplementation = gps_stage === BusinessProcessConstants.Implementation;
  const isApprovedToOffer = (gps_pirDecision || '')?.toLowerCase()?.includes('approved to offer');
  const isAdmin = canGPSUserHasInvestmentsAdmin();
  const isBPO = canGPSUserHasInvestmentsBPO();
  return isImplementation && isApprovedToOffer && !isAdmin && !isBPO;
}

export function isGPSEditorDisabledForFinalStages(
  gps_stage: string | undefined
): boolean {
  const isFinalStage =
    gps_stage === BusinessProcessConstants.Implementation ||
    gps_stage === BusinessProcessConstants.Completed;
  const isEditor = canGPSUserHasInvestmentsEdit();
  const isAdmin = canGPSUserHasInvestmentsAdmin();
  const isBPO = canGPSUserHasInvestmentsBPO();
  return isFinalStage && isEditor && !isAdmin && !isBPO;
}

export function isGPSRequestor(
  gps_business: string | undefined
): boolean {
  const isCommercial = gps_business === UATConstants.GPSInvestmentTitles.Commercial;
  const isRequestor = !(canGPSUserHasInvestmentsBPO() || canGPSUserHasInvestmentsEdit() || canGPSUserHasInvestmentsAdmin() || canGPSUserHasInvestmentsOwner());
  return isCommercial && isRequestor;
}

export const transformSGSRegionsForExport = (
  columns: Array<{
    InternalName?: string;
    internalName?: string;
    DisplayName?: string;
    displayName?: string;
    IsDefault?: boolean;
    isDefault?: boolean;
    CustomWidth?: number;
    customWidth?: number;
  }>,
    data: any[]
): SGSRegionsTransformResult => {
  const normalizedColumns = columns.map((col) => ({
    InternalName: col.InternalName ?? col.internalName ?? '',
    DisplayName: col.DisplayName ?? col.displayName ?? '',
    IsDefault: col.IsDefault ?? col.isDefault ?? false,
    CustomWidth: col.CustomWidth ?? col.customWidth ?? 0,
  }));

    // Extract SGS_Regions column definitions dynamically from input columns
  const sgsRegionsColumns = normalizedColumns.filter(
        (col) => col.InternalName.includes('fields/SGS_Regions/')
    );
    // Build dynamic column definitions from the SGS_Regions columns
    const dynamicColumnDefinitions = sgsRegionsColumns.map((col) => {
        // Extract field name from InternalName (e.g., "fields/SGS_Regions/DCRegionFrom" → "DCRegionFrom")
        const fieldName = col.InternalName.replace('fields/SGS_Regions/', '');
        // Remove trailing number from DisplayName (e.g., "DC Region From1" → "DC Region From")
        const displayName = col.DisplayName.replace(/\d+$/, '');
        return {
            fieldName,
            displayName,
            customWidth: col.CustomWidth
        };
    });

    // Filter out columns that contain "fields/SGS_Regions/"
    const filteredColumns = normalizedColumns.filter(
        (col) => !col.InternalName.includes('fields/SGS_Regions/')
    );
    
    // Get maximum SGS_Regions array length across all data items
    let maxRegionsCount = 0;
    data.forEach((item) => {
        const sgsRegions = item?.fields?.SGS_Regions;
        if (Array.isArray(sgsRegions) && sgsRegions.length > maxRegionsCount) {
            maxRegionsCount = sgsRegions.length;
        }
    });

    // Generate indexed columns for each SGS_Regions entry
    // Group by index first (all *1 columns, then all *2 columns, etc.)
    const indexedColumns: Array<{ InternalName: string; DisplayName: string; IsDefault: boolean; CustomWidth: number }> = [];
    for (let i = 0; i < maxRegionsCount; i++) {
        dynamicColumnDefinitions.forEach((colDef) => {
            indexedColumns.push({
                InternalName: `fields/${colDef.fieldName}_${i}`,
                DisplayName: `${colDef.displayName} ${i + 1}`,
                IsDefault: false,
                CustomWidth: colDef.customWidth
            });
        });
    }

    // Flatten SGS_Regions data in each item
      const transformedData = data.map((item) => {
        const sgsRegions = item?.fields?.SGS_Regions;
        const flattenedFields = { ...item.fields };
        if (!Array.isArray(sgsRegions) || sgsRegions.length === 0) {
            delete flattenedFields.SGS_Regions;
            return { ...item, fields: flattenedFields };
        }
        sgsRegions.forEach((region, index) => {
            dynamicColumnDefinitions.forEach((colDef) => {
                const key = `${colDef.fieldName}_${index}`;
                const value = region[colDef.fieldName] ?? null;
                // Handle array values (like PrimaryReasonForBlocked) by joining them
                flattenedFields[key] = Array.isArray(value) ? value.join(', ') : value || '';
            });
        });

        delete flattenedFields.SGS_Regions;
        return {  
            ...item,
            fields: flattenedFields
        };
    });
    return {
        transformedColumns: [...filteredColumns, ...indexedColumns],
        transformedData
    };
};

// Generated by GitHub Copilot | Updated on: 2026-04-07 | 18 lines modified | Added hasCorpGCSSAUserRole utility function for Edit in ADO visibility
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 12 lines modified | Added Editor final-stage lock
// Generated by GitHub Copilot | Updated on: 2026-10-26 | 11 lines added | Keep Completed date editing Admin-only
// Copilot change tracking: 2026-11-01 migration: Retain frozen release reconciliation.
// Copilot change tracking: 2026-11-02 frontend: Type existing authorization returns and tenant area-path accumulation.
// Copilot change tracking: 2026-11-02 frontend-tenant: Preserve authorization-only tenant results with an explicit optional-catalog contract.
